import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import type { DateValueType } from "react-tailwindcss-datepicker";
import {
  AlgoliaSearchResults,
  createPersonOnlyHitRenderer,
  createPersonOnlySearchUiState,
  getPersonDisplayName,
  InfoCard,
  isPersonExactMatch,
  useAlgoliaSearch,
  type PersonHit
} from "@/components/AlgoliaSearch";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem";
import Progress from "@/components/Progress";
import SideSheetModal from "@/components/SideSheetModal";
import SubSectionHeading from "@/components/SubSectionHeading";
import TextField from "@/components/TextField";
import { ListItemSkeleton } from "@/components/Skeleton";
import { showSnackbar } from "@/lib/snackbarUtils";
import { committeeListItemSupportingText, type CommitteeCatalogScope } from "../committeeScope";
import { COMMITTEE_ASSIGNMENTS_PAGE_SIZE, DEFAULT_COMMITTEE_ASSIGNMENT_TITLE } from "../constants";
import { isCurrentByEndDate } from "../currentMembers";
import { BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY, COMMITTEE_ASSIGNMENT_TITLES_QUERY } from "../queries";
import { BULK_CREATE_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION } from "../mutations";
import { getContinuationTermStartDate, getDefaultTermEndDate, getDefaultTermStartDate } from "../termDates";
import {
  DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY,
  type BackofficeCommitteeAssignmentsQueryResponse,
  type BackofficeCommitteeAssignmentsQueryVariables
} from "../types";
import CurrentCommitteeMembersList, { type CurrentCommitteeMemberRow } from "./CurrentCommitteeMembersList";
import DraftParticipantList, { type DraftParticipantRow } from "./DraftParticipantList";
import EditCommitteeAssignmentDialog, { type EditCommitteeAssignmentSaveInput } from "./EditCommitteeAssignmentDialog";

export type AddCommitteeParticipantsSheetProps = {
  open: boolean;
  committeeId?: string;
  committeeName: string;
  committeeScope?: CommitteeCatalogScope;
  sectionName?: string;
  onClose(): void;
};

const personSearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

function toIsoDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  if (value instanceof Date) return DateTime.fromJSDate(value).toISODate() ?? "";
  if (typeof value === "string") {
    const dt = DateTime.fromISO(value, { zone: "utc" });
    return dt.isValid ? (dt.toISODate() ?? "") : "";
  }
  return "";
}

function dateSelectStartToIso(value: DateValueType | string | null): string {
  if (value == null || typeof value === "string") return "";
  return toIsoDate(value.startDate);
}

function createDraftId() {
  return `draft-${crypto.randomUUID()}`;
}

export default function AddCommitteeParticipantsSheet({
  open,
  committeeId,
  committeeName,
  committeeScope = "national",
  sectionName,
  onClose
}: AddCommitteeParticipantsSheetProps) {
  const supportingText = committeeListItemSupportingText(committeeScope, { sectionName });
  const [termStartDate, setTermStartDate] = useState(getDefaultTermStartDate);
  const [termEndDate, setTermEndDate] = useState(getDefaultTermEndDate);
  const [draftParticipants, setDraftParticipants] = useState<DraftParticipantRow[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pagingFailed, setPagingFailed] = useState(false);
  const handleClearRef = useRef(() => {});
  const endBeforeStart = !!termStartDate && !!termEndDate && termEndDate < termStartDate;
  const todayIso = DateTime.utc().toISODate() ?? "";

  const { data: titlesData } = useQuery(COMMITTEE_ASSIGNMENT_TITLES_QUERY, {
    skip: !open
  });
  const defaultTitleId = useMemo(() => {
    const titles = titlesData?.committeeAssignmentTitles ?? [];
    return titles.find((title) => title.title === DEFAULT_COMMITTEE_ASSIGNMENT_TITLE)?.id ?? "";
  }, [titlesData?.committeeAssignmentTitles]);

  const [bulkCreateAssignments, { loading: saving }] = useMutation(
    BULK_CREATE_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION,
    {
      refetchQueries: "active"
    }
  );

  const draftPgaIds = useMemo(
    () => new Set(draftParticipants.map((participant) => participant.pgaId)),
    [draftParticipants]
  );
  const personHitRenderer = useMemo(() => createPersonOnlyHitRenderer(), []);
  const personSearchUiState = useMemo(() => createPersonOnlySearchUiState(personSearchIndexName), []);

  const assignmentQueryVariables = useMemo(
    (): BackofficeCommitteeAssignmentsQueryVariables => ({
      id: committeeId ?? "",
      first: COMMITTEE_ASSIGNMENTS_PAGE_SIZE,
      status: "ACTIVE",
      orderBy: DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY
    }),
    [committeeId]
  );

  const {
    data: assignmentsData,
    loading: assignmentsQueryLoading,
    fetchMore: fetchMoreAssignments
  } = useQuery<BackofficeCommitteeAssignmentsQueryResponse, BackofficeCommitteeAssignmentsQueryVariables>(
    BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY,
    {
      variables: assignmentQueryVariables,
      skip: !open || !committeeId,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true
    }
  );

  const fetchMoreAssignmentsRef = useRef(fetchMoreAssignments);
  fetchMoreAssignmentsRef.current = fetchMoreAssignments;
  const lastRequestedCursorRef = useRef<string | null>(null);
  const pagingInFlightRef = useRef(false);

  const assignmentsPageInfo = assignmentsData?.backofficeCommittee?.assignments?.pageInfo;
  const assignmentsStillPaging = !!assignmentsPageInfo?.hasNextPage && !pagingFailed;

  useEffect(() => {
    if (!open || !committeeId) {
      lastRequestedCursorRef.current = null;
      pagingInFlightRef.current = false;
      return;
    }
    setPagingFailed(false);
  }, [open, committeeId]);

  useEffect(() => {
    const cursor = assignmentsPageInfo?.endCursor;
    if (!open || !committeeId || pagingFailed || !assignmentsPageInfo?.hasNextPage || !cursor) {
      return;
    }
    if (pagingInFlightRef.current || lastRequestedCursorRef.current === cursor) {
      return;
    }

    lastRequestedCursorRef.current = cursor;
    pagingInFlightRef.current = true;

    void fetchMoreAssignmentsRef
      .current({
        variables: {
          ...assignmentQueryVariables,
          after: cursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.backofficeCommittee?.assignments) return previousResult;

          const previousAssignments = previousResult.backofficeCommittee?.assignments;
          const nextAssignments = fetchMoreResult.backofficeCommittee.assignments;
          if (!previousAssignments) return fetchMoreResult;

          return {
            ...fetchMoreResult,
            backofficeCommittee: {
              ...fetchMoreResult.backofficeCommittee,
              assignments: {
                ...nextAssignments,
                nodes: [...(previousAssignments.nodes ?? []), ...(nextAssignments.nodes ?? [])]
              }
            }
          };
        }
      })
      .then((result) => {
        const nextPageInfo = result.data?.backofficeCommittee?.assignments?.pageInfo;
        if (!result.data || (nextPageInfo?.hasNextPage && nextPageInfo.endCursor === cursor)) {
          setPagingFailed(true);
        }
      })
      .catch(() => {
        setPagingFailed(true);
      })
      .finally(() => {
        pagingInFlightRef.current = false;
      });
  }, [
    open,
    committeeId,
    pagingFailed,
    assignmentQueryVariables,
    assignmentsPageInfo?.hasNextPage,
    assignmentsPageInfo?.endCursor
  ]);

  const assignmentsLoading = assignmentsQueryLoading || assignmentsStillPaging;
  const searchDisabled = (!!committeeId && assignmentsLoading) || !defaultTitleId;

  const currentMembers = useMemo((): CurrentCommitteeMemberRow[] => {
    const nodes = assignmentsData?.backofficeCommittee?.assignments?.nodes ?? [];

    return nodes.flatMap((assignment) => {
      const personId = assignment.person?.id;
      const pgaId = assignment.person?.pgaId;
      const startDate = assignment.startDate;
      const endDate = assignment.endDate;
      if (!personId || !pgaId || !startDate || !isCurrentByEndDate(endDate, todayIso)) return [];

      return [
        {
          assignmentId: assignment.id,
          personId,
          pgaId,
          displayName: assignment.person?.displayName || `PGA ${pgaId}`,
          profilePhoto: assignment.person?.profilePhoto ?? null,
          position: assignment.title.title,
          titleId: assignment.title.id,
          startDate,
          endDate: endDate ?? null
        }
      ];
    });
  }, [assignmentsData?.backofficeCommittee?.assignments?.nodes, todayIso]);

  const availableCurrentMembers = useMemo(
    () => currentMembers.filter((member) => !draftPgaIds.has(member.pgaId)),
    [currentMembers, draftPgaIds]
  );
  const currentMembersByPgaId = useMemo(
    () => new Map(currentMembers.map((member) => [member.pgaId, member])),
    [currentMembers]
  );

  const {
    query,
    displayValue,
    results,
    isLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<PersonHit>({
    indexName: personSearchIndexName,
    hitsPerPage: 7,
    initialUiState: personSearchUiState,
    getDisplayValue: getPersonDisplayName,
    isExactMatch: isPersonExactMatch,
    filterHits: (hits) => hits.filter((hit) => !draftPgaIds.has(hit.pga_id)),
    onSelect: (hit) => {
      if (endBeforeStart || draftPgaIds.has(hit.pga_id) || searchDisabled) return;

      const continuingMember = currentMembersByPgaId.get(hit.pga_id);
      const titleId = continuingMember?.titleId || defaultTitleId;

      setDraftParticipants((current) => {
        if (current.some((participant) => participant.pgaId === hit.pga_id)) return current;

        return [
          ...current,
          {
            draftId: createDraftId(),
            personId: hit.objectID,
            pgaId: hit.pga_id,
            displayName: getPersonDisplayName(hit),
            profilePhoto: hit.profile_photo ?? null,
            position: continuingMember?.position ?? DEFAULT_COMMITTEE_ASSIGNMENT_TITLE,
            titleId,
            startDate: continuingMember?.startDate ?? termStartDate,
            endDate: termEndDate,
            ...(continuingMember
              ? {
                  continuationOf: {
                    startDate: continuingMember.startDate,
                    endDate: continuingMember.endDate
                  }
                }
              : {})
          }
        ];
      });
      handleClearRef.current();
    }
  });
  handleClearRef.current = handleClear;

  useEffect(() => {
    if (!open) return;

    setTermStartDate(getDefaultTermStartDate());
    setTermEndDate(getDefaultTermEndDate());
    setDraftParticipants([]);
    setEditingDraftId(null);
    setSaveError(null);
    handleClearRef.current();
  }, [open]);

  const editingParticipant =
    editingDraftId == null
      ? null
      : (draftParticipants.find((participant) => participant.draftId === editingDraftId) ?? null);

  const saveDraftParticipant = (input: EditCommitteeAssignmentSaveInput) => {
    setDraftParticipants((current) =>
      current.map((participant) => {
        if (participant.draftId !== input.id) return participant;

        const keptContinuation =
          participant.continuationOf && input.startDate === participant.continuationOf.startDate
            ? participant.continuationOf
            : undefined;

        return {
          ...participant,
          position: input.title,
          titleId: input.titleId,
          startDate: input.startDate,
          endDate: input.endDate ?? "",
          continuationOf: keptContinuation
        };
      })
    );
    setEditingDraftId(null);
  };

  const addCurrentMember = (member: CurrentCommitteeMemberRow) => {
    if (endBeforeStart) return;

    setDraftParticipants((current) => {
      if (current.some((participant) => participant.pgaId === member.pgaId)) return current;

      return [
        ...current,
        {
          draftId: createDraftId(),
          personId: member.personId,
          pgaId: member.pgaId,
          displayName: member.displayName,
          profilePhoto: member.profilePhoto,
          position: member.position,
          titleId: member.titleId,
          startDate: member.startDate,
          endDate: termEndDate,
          continuationOf: {
            startDate: member.startDate,
            endDate: member.endDate
          }
        }
      ];
    });
  };

  const handleSave = async () => {
    if (!committeeId || draftParticipants.length === 0 || endBeforeStart || saving) return;

    setSaveError(null);

    try {
      const { data } = await bulkCreateAssignments({
        variables: {
          input: {
            committeeId,
            assignments: draftParticipants.map((participant) => ({
              personId: participant.personId,
              titleId: participant.titleId,
              startDate: participant.continuationOf
                ? getContinuationTermStartDate(participant.continuationOf, termStartDate)
                : participant.startDate,
              endDate: participant.endDate || null
            }))
          }
        }
      });

      const result = data?.bulkCreateBackofficeCommitteeAssignments;
      const failures = result?.failedAssignments ?? [];
      const failureLines = failures.map((failure) => failure.message);
      const successfulPersonIds = new Set(
        (result?.successfulAssignments ?? []).map((assignment) => assignment.person.id)
      );

      if (successfulPersonIds.size > 0 && failureLines.length > 0) {
        setDraftParticipants((current) =>
          current.filter((participant) => !successfulPersonIds.has(participant.personId))
        );
        setSaveError(
          `${result?.message ?? "Some committee assignments could not be created"}\n${failureLines.join("\n")}`
        );
        return;
      }

      if (!result?.success || failureLines.length > 0) {
        const message = result?.message ?? "Unable to save committee participants";
        setSaveError(failureLines.length > 0 ? `${message}\n${failureLines.join("\n")}` : message);
        return;
      }

      showSnackbar(result.message || "Committee participants saved");
      onClose();
    } catch (error) {
      setSaveError((error as Error).message);
    }
  };

  const hasActiveQuery = query.trim() !== "";
  const showSearchLoading = !searchDisabled && hasActiveQuery && isLoading && results.length === 0;
  const showSearchEmpty = !searchDisabled && hasActiveQuery && !isLoading && results.length === 0;
  const showSearchResults = !searchDisabled && hasActiveQuery && !isLoading && results.length > 0;
  const showCurrentMembersLoading = !!committeeId && assignmentsLoading;
  const showCurrentMembers = !!committeeId && !assignmentsLoading && availableCurrentMembers.length > 0;

  const searchTrailingIcon =
    displayValue || query ? (
      <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const termStartDateValue = useMemo(() => ({ startDate: termStartDate, endDate: termStartDate }), [termStartDate]);
  const termEndDateValue = useMemo(() => ({ startDate: termEndDate, endDate: termEndDate }), [termEndDate]);

  return (
    <SideSheetModal
      isSidebarOpen={open}
      setSidebarOpen={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title="Add People"
      disableClose={saving}
      disableFocusManagement
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving} testId="add-committee-participants-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            disabled={draftParticipants.length === 0 || endBeforeStart || saving || !committeeId}
            onClick={handleSave}
            testId="add-committee-participants-save"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Progress size={20} color="currentColor" />
                Saving...
              </span>
            ) : (
              "Save"
            )}
          </Button>
        </>
      }
    >
      <div data-testid="add-committee-participants-sheet">
        {saveError && (
          <Banner
            className="mt-4"
            variant="error"
            inline
            description={saveError}
            testId="add-committee-participants-save-error"
            closable
            onClose={() => setSaveError(null)}
          />
        )}
        <div className="mt-4" data-testid="add-committee-participants-committee">
          <ListItem compact useAvatar iconName="groups" headline={committeeName} supportingText={supportingText} />
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2" data-testid="add-committee-participants-term-dates">
          <DateSelect
            label="Default Start Date"
            value={termStartDateValue}
            onChange={(value) => {
              const startDate = dateSelectStartToIso(value);
              if (!startDate) return;
              setTermStartDate(startDate);
            }}
            useSingleDate
            useMultiCalendar={false}
            useModal
            showFooter={false}
          />
          <DateSelect
            label="Default End Date"
            value={termEndDateValue}
            onChange={(value) => {
              const endDate = dateSelectStartToIso(value);
              if (!endDate) return;
              setTermEndDate(endDate);
            }}
            useSingleDate
            useMultiCalendar={false}
            useModal
            showFooter={false}
            error={endBeforeStart}
            errorMessage={endBeforeStart ? "End date cannot be earlier than start date" : undefined}
          />
        </div>

        <div className="mt-6" data-testid="add-committee-participants-search" ref={searchBoxRef}>
          <TextField
            label="People"
            value={displayValue || query}
            onChangeText={(value) => {
              setDisplayValue(undefined);
              handleQueryChange(value);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            leadingIcon="search"
            trailingIcon={searchTrailingIcon}
            variant="outlined"
            className="w-full"
            disabled={searchDisabled}
            supportingText="Search for people to build the new term roster."
          />
        </div>

        {showSearchLoading && (
          <div className="mt-4 py-3 text-center text-sys-brand-on-surface-variant">Searching...</div>
        )}

        {showSearchEmpty && (
          <div className="mt-4">
            <InfoCard>No people found</InfoCard>
          </div>
        )}

        {showSearchResults && (
          <AlgoliaSearchResults
            results={results}
            query={query}
            isLoading={false}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            handleItemSelected={handleItemSelected}
            itemRefs={itemRefs}
            resultsContainerRef={resultsContainerRef}
            renderHit={personHitRenderer}
            isExactMatch={isPersonExactMatch}
            className="mt-4"
          />
        )}

        {draftParticipants.length > 0 && (
          <>
            <div
              className="sticky -top-px z-10 mt-6 pt-px pb-2 bg-sys-brand-surface-container-low"
              data-testid="add-committee-participants-draft-count"
            >
              <SubSectionHeading>People to Add ({draftParticipants.length})</SubSectionHeading>
            </div>
            <DraftParticipantList
              participants={draftParticipants}
              onEdit={setEditingDraftId}
              onRemove={(draftId) =>
                setDraftParticipants((current) => current.filter((participant) => participant.draftId !== draftId))
              }
            />
          </>
        )}

        {showCurrentMembersLoading && (
          <div data-testid="add-committee-participants-assignments-loading">
            <div className="mt-6 pb-2" data-testid="add-committee-participants-current-heading">
              <SubSectionHeading>Current Participants</SubSectionHeading>
              <p className="mt-1 text-body-medium text-sys-brand-on-surface-variant">
                Add continuing participants to the new term. Original start dates are preserved when someone has served
                on earlier cycles.
              </p>
            </div>
            <ListItemSkeleton rows={3} className="mt-3 px-2" />
          </div>
        )}

        {showCurrentMembers && (
          <>
            <div className="mt-6 pb-2" data-testid="add-committee-participants-current-heading">
              <SubSectionHeading>Current Participants ({availableCurrentMembers.length})</SubSectionHeading>
              <p className="mt-1 text-body-medium text-sys-brand-on-surface-variant">
                Add continuing participants to the new term. Original start dates are preserved when someone has served
                on earlier cycles.
              </p>
            </div>
            <CurrentCommitteeMembersList
              members={availableCurrentMembers}
              addDisabled={endBeforeStart}
              onAdd={addCurrentMember}
            />
          </>
        )}
      </div>

      <EditCommitteeAssignmentDialog
        open={editingParticipant != null}
        member={
          editingParticipant
            ? {
                id: editingParticipant.draftId,
                startDate: editingParticipant.startDate,
                endDate: editingParticipant.endDate,
                title: { id: editingParticipant.titleId, title: editingParticipant.position },
                person: {
                  pgaId: editingParticipant.pgaId,
                  displayName: editingParticipant.displayName,
                  profilePhoto: editingParticipant.profilePhoto
                },
                supportingText: committeeName
              }
            : null
        }
        onClose={() => setEditingDraftId(null)}
        onSave={saveDraftParticipant}
        saveLabel="Continue"
      />
    </SideSheetModal>
  );
}
